import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * User type for checkout flow
 * - guest: User continues without registration
 * - registered: User is logged in
 * - pending: User hasn't made a choice yet
 */
export type CheckoutUserType = "guest" | "registered" | "pending";

/**
 * Fulfillment type for the order
 */
export type FulfillmentType = "pickup" | "delivery";

/**
 * Payment method type
 * - pay_at_collection: Pay when picking up
 * - online: Stripe payment
 * - cash_on_delivery: Cash payment at delivery
 * - card_on_delivery: Card payment at delivery
 */
export type PaymentMethod =
  | "pay_at_collection"
  | "online"
  | "cash_on_delivery"
  | "card_on_delivery";

/**
 * Checkout step IDs
 */
export enum CheckoutStep {
  CART = 1,
  USER_INFO = 2,
  // DELIVERY_CHECK = 3,
  PLACE_ORDER = 3,
}

/**
 * Guest customer info for checkout
 */
export interface GuestCustomerInfo {
  customer_name: string;
  customer_email: string;
  customer_phone: string;
}

/**
 * Registered user profile info (from API)
 */
export interface RegisteredUserInfo {
  customer_id?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}

/**
 * Delivery address info
 */
export interface DeliveryAddress {
  address_id?: string;
  postcode?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
}

/**
 * Delivery zone check result
 */
export interface DeliveryZoneResult {
  is_deliverable: boolean;
  delivery_fee?: number;
  estimated_time?: string;
  message?: string;
}

/**
 * Checkout state interface
 */
export interface CheckoutState {
  // Stepper state
  currentStep: CheckoutStep;
  completedSteps: CheckoutStep[];

  // User type
  userType: CheckoutUserType;
  showAuthCheck: boolean;

  // Guest info
  guestInfo: GuestCustomerInfo;

  // Registered user info (from profile API)
  registeredUserInfo: RegisteredUserInfo | null;

  // Fulfillment
  fulfillmentType: FulfillmentType;

  // Delivery
  deliveryAddress: DeliveryAddress;
  deliveryZoneResult: DeliveryZoneResult | null;
  isCheckingDelivery: boolean;
  selectedAddressId: string | null; // For registered users - send this instead of full address

  // Payment
  paymentMethod: PaymentMethod;

  // Order placement
  isPlacingOrder: boolean;
  orderError: string | null;
  orderId: string | null;
}

const initialState: CheckoutState = {
  // Stepper
  currentStep: CheckoutStep.CART,
  completedSteps: [],

  // User type
  userType: "pending",
  showAuthCheck: true,

  // Guest info
  guestInfo: {
    customer_name: "",
    customer_email: "",
    customer_phone: "",
  },

  // Registered user info
  registeredUserInfo: null,

  // Fulfillment
  fulfillmentType: "pickup",

  // Delivery
  deliveryAddress: {},
  deliveryZoneResult: null,
  isCheckingDelivery: false,
  selectedAddressId: null,

  // Payment
  paymentMethod: "pay_at_collection",

  // Order
  isPlacingOrder: false,
  orderError: null,
  orderId: null,
};

const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    // Step navigation
    setCurrentStep: (state, action: PayloadAction<CheckoutStep>) => {
      state.currentStep = action.payload;
    },

    goToNextStep: (state) => {
      if (state.currentStep < CheckoutStep.PLACE_ORDER) {
        // Mark current step as completed
        if (!state.completedSteps.includes(state.currentStep)) {
          state.completedSteps.push(state.currentStep);
        }
        state.currentStep = state.currentStep + 1;
      }
    },

    goToPreviousStep: (state) => {
      if (state.currentStep > CheckoutStep.CART) {
        state.currentStep = state.currentStep - 1;
      }
    },

    markStepCompleted: (state, action: PayloadAction<CheckoutStep>) => {
      if (!state.completedSteps.includes(action.payload)) {
        state.completedSteps.push(action.payload);
      }
    },

    // User type management
    setUserType: (state, action: PayloadAction<CheckoutUserType>) => {
      state.userType = action.payload;
      // Hide auth check once user has made a choice
      if (action.payload !== "pending") {
        state.showAuthCheck = false;
      }
    },

    setShowAuthCheck: (state, action: PayloadAction<boolean>) => {
      state.showAuthCheck = action.payload;
    },

    // Guest info
    setGuestInfo: (
      state,
      action: PayloadAction<Partial<GuestCustomerInfo>>,
    ) => {
      state.guestInfo = { ...state.guestInfo, ...action.payload };
    },

    clearGuestInfo: (state) => {
      state.guestInfo = initialState.guestInfo;
    },

    // Registered user info
    setRegisteredUserInfo: (
      state,
      action: PayloadAction<RegisteredUserInfo | null>,
    ) => {
      state.registeredUserInfo = action.payload;
    },

    // Fulfillment
    setFulfillmentType: (state, action: PayloadAction<FulfillmentType>) => {
      state.fulfillmentType = action.payload;
    },

    // Delivery
    setDeliveryAddress: (state, action: PayloadAction<DeliveryAddress>) => {
      state.deliveryAddress = action.payload;
    },

    setDeliveryZoneResult: (
      state,
      action: PayloadAction<DeliveryZoneResult | null>,
    ) => {
      state.deliveryZoneResult = action.payload;
    },

    setIsCheckingDelivery: (state, action: PayloadAction<boolean>) => {
      state.isCheckingDelivery = action.payload;
    },

    setSelectedAddressId: (state, action: PayloadAction<string | null>) => {
      state.selectedAddressId = action.payload;
    },

    // Payment
    setPaymentMethod: (state, action: PayloadAction<PaymentMethod>) => {
      state.paymentMethod = action.payload;
    },

    // Order placement
    startPlacingOrder: (state) => {
      state.isPlacingOrder = true;
      state.orderError = null;
    },

    orderPlacementSuccess: (state, action: PayloadAction<string>) => {
      state.isPlacingOrder = false;
      state.orderId = action.payload;
      state.orderError = null;
    },

    orderPlacementFailure: (state, action: PayloadAction<string>) => {
      state.isPlacingOrder = false;
      state.orderError = action.payload;
    },

    // Reset checkout
    resetCheckout: () => initialState,
  },
});

export const {
  setCurrentStep,
  goToNextStep,
  goToPreviousStep,
  markStepCompleted,
  setUserType,
  setShowAuthCheck,
  setGuestInfo,
  clearGuestInfo,
  setRegisteredUserInfo,
  setFulfillmentType,
  setDeliveryAddress,
  setDeliveryZoneResult,
  setIsCheckingDelivery,
  setSelectedAddressId,
  setPaymentMethod,
  startPlacingOrder,
  orderPlacementSuccess,
  orderPlacementFailure,
  resetCheckout,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
